import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import InlineProgress from '../components/InlineProgress';

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '20px',
  padding: theme.spacing(1, 3),
  textTransform: 'none',
  fontWeight: 600,
}));

const MoSCoWChip = styled(Chip)(({ theme, category }) => {
    const colors = {
      Must: '#D32F2F',    // Red
      Should: '#FFA000',  // Amber
      Could: '#388E3C',   // Green
      'Won\'t': '#757575', // Grey
    };
    return {
      backgroundColor: colors[category] || theme.palette.grey[500],
      color: '#FFFFFF',
      fontWeight: 600,
      fontSize: '0.875rem',
      '& .MuiChip-label': {
        padding: '0 10px',
      },
    };
  });

const VoteChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.secondary.contrastText,
  fontWeight: 600,
  fontSize: '0.875rem',
}));

function PerformMoSCoWAnalysisStep({ data, updateWorkshop }) {
  const handleContinue = () => {
    updateWorkshop(data.id, { nextStep: 'defineMVPFeatures' });
  };

  return (
    <Box component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        MoSCoW Analysis Results
      </Typography>
      {data.nextStep === 'performMoSCoWAnalysis' ? (
        <InlineProgress message="Performing MoSCoW analysis..." />
      ) : data.moscowAnalyzedFeatures ? (
        <>
          <AnimatePresence>
            <List>
              {data.moscowAnalyzedFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <StyledPaper elevation={3} sx={{ mb: 3, p: 3 }}>
                    <ListItem alignItems="flex-start" sx={{ flexDirection: 'column', p: 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1 }}>
                        <Typography variant="h6">{feature.shortDescription}</Typography>
                        <Box>
                          <MoSCoWChip label={feature.moscowCategory} category={feature.moscowCategory} sx={{ mr: 1 }} />
                          <VoteChip label={`${feature.votes} votes`} icon={<HowToVoteIcon />} />
                        </Box>
                      </Box>
                      <ListItemText
                        secondary={
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {feature.longDescription}
                          </Typography>
                        }
                      />
                    </ListItem>
                  </StyledPaper>
                </motion.div>
              ))}
            </List>
          </AnimatePresence>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <StyledButton
              variant="contained"
              color="primary"
              onClick={handleContinue}
              endIcon={<ArrowForwardIcon />}
            >
              Continue to Define MVP Features
            </StyledButton>
          </Box>
        </>
      ) : (
        <Typography variant="body1" color="text.secondary">
          No MoSCoW analyzed features available yet.
        </Typography>
      )}
    </Box>
  );
}

export default PerformMoSCoWAnalysisStep;